import { CommissionSummary } from '@base/src/app/models/renditions/commission-summary.model';
import { InvoiceRendition } from '@base/src/app/models/renditions/invoices.model';

export interface AgentOfficeCapContextResult {
  poolAgentNames: string[];
  expensesCap: number;
}

export function isSellerSideClient(client: string): boolean {
  return client === 'Vendedor' || client === 'Captador';
}

export function isTeamLeaderBillable(teamLeader?: {
  name?: string | null;
  netCommission?: { usd?: number; local?: number } | null;
  rawCommission?: { usd?: number; local?: number } | null;
}): boolean {
  if (!teamLeader?.name?.trim()) {
    return false;
  }

  return (
    (teamLeader.netCommission?.usd ?? 0) > 0 ||
    (teamLeader.netCommission?.local ?? 0) > 0 ||
    (teamLeader.rawCommission?.usd ?? 0) > 0 ||
    (teamLeader.rawCommission?.local ?? 0) > 0
  );
}

export function invoiceMatchesSellerClient(client: string): boolean {
  return isSellerSideClient(client);
}

export function invoiceMatchesBuyerClient(
  client: string,
  isNodeDo = false,
): boolean {
  return isNodeDo ? client === 'Vendedor' : client === 'Comprador';
}

export function calculateAgentExpensesCap(
  netCommissionUsd: number,
  netCommissionLocal: number,
  typeOriginal: string,
  exchangeRate: number,
  previsional: number,
  inLocalCurrency = false,
): number {
  const RATE = exchangeRate || 1;
  // Avoid (usd + local/rate) * rate — that float round-trip marks exact local caps as over the limit.
  const NET = inLocalCurrency
    ? netCommissionUsd * RATE + netCommissionLocal
    : netCommissionUsd + netCommissionLocal / RATE;

  if (typeOriginal !== 'RAPP') {
    return NET / (1 + previsional / 100);
  }

  return NET;
}

export function calculateTeamLeaderExpensesCap(
  netCommissionUsd: number,
  netCommissionLocal: number,
  exchangeRate: number,
  previsional: number,
  inLocalCurrency = false,
): number {
  const RATE = exchangeRate || 1;
  const NET = inLocalCurrency
    ? netCommissionUsd * RATE + netCommissionLocal
    : netCommissionUsd + netCommissionLocal / RATE;

  return NET / (1 + previsional / 100);
}

export function getAmountAbaoFromInvoice(
  invoice: InvoiceRendition | undefined,
  exchangeRate: number,
  previsional: number,
): number {
  if (!invoice) {
    return 0;
  }

  const AMOUNT_INVOICE =
    invoice.amountCurrency === 'USD' || invoice.amountCurrency === 'U$S'
      ? invoice.amount
      : invoice.amount / (exchangeRate || 1);

  return Math.trunc(AMOUNT_INVOICE * (previsional / 100) * 100) / 100;
}

function withAbaoForCap(
  netUsd: number,
  netLocal: number,
  abaoUsd: number,
  abaoLocal: number,
  invoiceAbaoAmount: number,
  useCheckoutAbaoAmount: boolean,
): { usd: number; local: number } {
  if (useCheckoutAbaoAmount) {
    return {
      usd: netUsd + (abaoUsd > 0 ? abaoUsd : 0),
      local: netLocal + (abaoLocal > 0 ? abaoLocal : 0),
    };
  }

  return {
    usd: netUsd + (abaoUsd > 0 ? invoiceAbaoAmount : 0),
    local: netLocal + (abaoLocal > 0 ? invoiceAbaoAmount : 0),
  };
}

export function getAgentOfficeCapContext(input: {
  checkout: CommissionSummary;
  client: string;
  selectedAgent: string;
  exchangeRate: number;
  previsional: number;
  invoiceAbaoAmount?: number;
  useCheckoutAbaoAmount?: boolean;
  inLocalCurrency?: boolean;
}): AgentOfficeCapContextResult {
  const CLIENT = input.client;
  const AGENT = input.selectedAgent;
  const INVOICE_ABAO_AMOUNT = input.invoiceAbaoAmount ?? 0;
  const USE_CHECKOUT_ABAO = input.useCheckoutAbaoAmount ?? false;
  const IN_LOCAL_CURRENCY = input.inLocalCurrency ?? false;
  const CHECKOUT = input.checkout;

  const REFERRED_AGENT = isSellerSideClient(CLIENT)
    ? CHECKOUT.seller.referred
    : CHECKOUT.buyer.referred;
  const AGENT_NET_COMMISSION = isSellerSideClient(CLIENT)
    ? CHECKOUT.seller.agent
    : CHECKOUT.buyer.agent;
  const TEAM_LEADER_AGENT = isSellerSideClient(CLIENT)
    ? CHECKOUT.seller.teamLeader
    : CHECKOUT.buyer.teamLeader;

  const REFERIDO_A_COBRAR = withAbaoForCap(
    CHECKOUT.referred.netCommission.usd,
    CHECKOUT.referred.netCommission.local,
    CHECKOUT.referred.abao.usd,
    CHECKOUT.referred.abao.local,
    INVOICE_ABAO_AMOUNT,
    USE_CHECKOUT_ABAO,
  );

  const AGENT_NET = withAbaoForCap(
    AGENT_NET_COMMISSION.netCommission.usd,
    AGENT_NET_COMMISSION.netCommission.local,
    AGENT_NET_COMMISSION.abao.usd,
    AGENT_NET_COMMISSION.abao.local,
    INVOICE_ABAO_AMOUNT,
    USE_CHECKOUT_ABAO,
  );

  const REFERRED_NET = withAbaoForCap(
    REFERRED_AGENT.netCommission.usd,
    REFERRED_AGENT.netCommission.local,
    REFERRED_AGENT.abao.usd,
    REFERRED_AGENT.abao.local,
    INVOICE_ABAO_AMOUNT,
    USE_CHECKOUT_ABAO,
  );

  const TEAM_LEADER_NET = withAbaoForCap(
    TEAM_LEADER_AGENT.netCommission.usd,
    TEAM_LEADER_AGENT.netCommission.local,
    TEAM_LEADER_AGENT.abao.usd,
    TEAM_LEADER_AGENT.abao.local,
    INVOICE_ABAO_AMOUNT,
    USE_CHECKOUT_ABAO,
  );

  const NET_COMMISSION_USD = CHECKOUT.referred.isReferred
    ? REFERIDO_A_COBRAR.usd
    : AGENT_NET.usd;
  const NET_COMMISSION_LOCAL = CHECKOUT.referred.isReferred
    ? REFERIDO_A_COBRAR.local
    : AGENT_NET.local;

  const AGENT_CAP = calculateAgentExpensesCap(
    NET_COMMISSION_USD,
    NET_COMMISSION_LOCAL,
    AGENT_NET_COMMISSION.type.original,
    input.exchangeRate,
    input.previsional,
    IN_LOCAL_CURRENCY,
  );
  const TEAM_LEADER_CAP = calculateTeamLeaderExpensesCap(
    TEAM_LEADER_NET.usd,
    TEAM_LEADER_NET.local,
    input.exchangeRate,
    input.previsional,
    IN_LOCAL_CURRENCY,
  );
  const REFERRED_CAP = calculateAgentExpensesCap(
    REFERRED_NET.usd,
    REFERRED_NET.local,
    REFERRED_AGENT.type.original,
    input.exchangeRate,
    input.previsional,
    IN_LOCAL_CURRENCY,
  );

  const HAS_TEAM_LEADER = isTeamLeaderBillable(TEAM_LEADER_AGENT);

  if (AGENT === REFERRED_AGENT.name && REFERRED_AGENT.name) {
    return {
      poolAgentNames: [REFERRED_AGENT.name],
      expensesCap: REFERRED_CAP,
    };
  }

  if (
    HAS_TEAM_LEADER &&
    (AGENT === AGENT_NET_COMMISSION.name || AGENT === TEAM_LEADER_AGENT.name)
  ) {
    return {
      poolAgentNames: [AGENT_NET_COMMISSION.name, TEAM_LEADER_AGENT.name],
      expensesCap: AGENT_CAP + TEAM_LEADER_CAP,
    };
  }

  if (
    AGENT === TEAM_LEADER_AGENT.name &&
    AGENT !== AGENT_NET_COMMISSION.name
  ) {
    return {
      poolAgentNames: [TEAM_LEADER_AGENT.name],
      expensesCap: TEAM_LEADER_CAP,
    };
  }

  if (AGENT === AGENT_NET_COMMISSION.name) {
    return {
      poolAgentNames: [AGENT_NET_COMMISSION.name],
      expensesCap: AGENT_CAP,
    };
  }

  return {
    poolAgentNames: AGENT ? [AGENT] : [],
    expensesCap: 0,
  };
}

export function getAgentOfficePoolCapContext(input: {
  checkout: CommissionSummary;
  side: 'seller' | 'buyer';
  agentFormName: string;
  teamLeaderFormName: string;
  exchangeRate: number;
  previsional: number;
  isReferidoACobrar?: boolean;
  agentNetCommissionUsd?: number;
  agentNetCommissionLocal?: number;
}): AgentOfficeCapContextResult {
  const SIDE_DATA =
    input.side === 'seller' ? input.checkout.seller : input.checkout.buyer;
  const AGENT = SIDE_DATA.agent;
  const TEAM_LEADER = SIDE_DATA.teamLeader;
  const AGENT_NAME = input.agentFormName || AGENT.name;
  const TEAM_LEADER_NAME = input.teamLeaderFormName || TEAM_LEADER.name;
  const USE_REFERRED_NET =
    input.checkout.referred.isReferred || !!input.isReferidoACobrar;
  const AGENT_NET_COMMISSION_USD =
    input.agentNetCommissionUsd ?? AGENT.netCommission.usd;
  const AGENT_NET_COMMISSION_LOCAL =
    input.agentNetCommissionLocal ?? AGENT.netCommission.local;

  const AGENT_NET = USE_REFERRED_NET
    ? withAbaoForCap(
        input.checkout.referred.netCommission.usd,
        input.checkout.referred.netCommission.local,
        input.checkout.referred.abao.usd,
        input.checkout.referred.abao.local,
        0,
        true,
      )
    : withAbaoForCap(
        AGENT_NET_COMMISSION_USD,
        AGENT_NET_COMMISSION_LOCAL,
        AGENT.abao.usd,
        AGENT.abao.local,
        0,
        true,
      );

  const TEAM_LEADER_NET = withAbaoForCap(
    TEAM_LEADER.netCommission.usd,
    TEAM_LEADER.netCommission.local,
    TEAM_LEADER.abao.usd,
    TEAM_LEADER.abao.local,
    0,
    true,
  );

  const AGENT_CAP = calculateAgentExpensesCap(
    AGENT_NET.usd,
    AGENT_NET.local,
    AGENT.type.original,
    input.exchangeRate,
    input.previsional,
  );
  const TEAM_LEADER_CAP = calculateTeamLeaderExpensesCap(
    TEAM_LEADER_NET.usd,
    TEAM_LEADER_NET.local,
    input.exchangeRate,
    input.previsional,
  );

  const HAS_TEAM_LEADER = isTeamLeaderBillable({
    name: TEAM_LEADER_NAME,
    netCommission: TEAM_LEADER.netCommission,
    rawCommission: TEAM_LEADER.rawCommission,
  });

  if (HAS_TEAM_LEADER) {
    return {
      poolAgentNames: [AGENT_NAME, TEAM_LEADER_NAME].filter(Boolean),
      expensesCap: AGENT_CAP + TEAM_LEADER_CAP,
    };
  }

  return {
    poolAgentNames: AGENT_NAME ? [AGENT_NAME] : [],
    expensesCap: AGENT_CAP,
  };
}

export function sumInvoiceAmountsUsd(
  invoices: InvoiceRendition[],
  exchangeRate: number,
): number {
  return invoices.reduce((acc: number, item: InvoiceRendition) => {
    const NEXT =
      acc +
      Number(
        item.amountCurrency === 'Local'
          ? item.amount / (exchangeRate || 1)
          : item.amount,
      );

    return Math.trunc(NEXT * 100) / 100;
  }, 0);
}

export function invoicesWithinCap(
  invoices: InvoiceRendition[],
  capUsd: number,
  exchangeRate: number,
): boolean {
  return capUsd >= sumInvoiceAmountsUsd(invoices, exchangeRate);
}

export function filterAgentOfficeInvoicesForPool(
  invoices: InvoiceRendition[],
  poolAgentNames: string[],
  matchesClient: (client: string) => boolean,
): InvoiceRendition[] {
  return invoices.filter(
    (e: InvoiceRendition) =>
      poolAgentNames.includes(e.agent) && matchesClient(e.client),
  );
}
